import type { DashboardSession } from '../../types/dashboard';
import { Avatar, Badge, StatusBadge, Tooltip } from '../ui';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { bookingStateToStatus } from '../../utils/sessionStatus';
import { formatLocalTime, methodToLocation } from '../../utils/sessionTime';
import { formatCurrency } from '../../utils/currency';

interface SessionRowProps {
  session: DashboardSession;
  onClick?: () => void;
}

function getInitials(clientName: string): string {
  const parts = clientName.split(' ');
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function SessionRow({ session, onClick }: SessionRowProps) {
  const isMd = useMediaQuery('(min-width: 768px)');
  const statusInfo = bookingStateToStatus(session.status);

  // Stored times are naive-UTC; show them in the viewer's local timezone.
  const localTime = formatLocalTime(session.date, session.startTime);

  if (!isMd) {
    // Mobile card layout
    return (
      <div 
        className="px-4 py-3.5 hover:bg-paper-2 transition cursor-pointer"
        onClick={onClick}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-display text-[16px] font-medium num">
              {localTime}
            </span>
            <span className="text-[11px] text-ink-4">
              {session.durationMinutes} min · {methodToLocation(session.format)}
            </span>
          </div>
          <StatusBadge label={statusInfo.label} tone={statusInfo.tone} />
        </div>
        <div className="flex items-center gap-2.5 mb-2">
          <Avatar
            initials={getInitials(session.clientName)}
            className={session.clientIsNew ? 'bg-tide text-paper-hi' : ''}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[13.5px] font-medium truncate">
                {session.clientName}
              </span>
              {session.clientIsNew && <Badge variant="spark">New</Badge>}
            </div>
            <div
              className={`text-[11.5px] truncate ${session.alertNote ? 'text-rust' : 'text-ink-4'}`}
            >
              {session.alertNote ?? session.clientNote ?? ''}
            </div>
          </div>
          <Tooltip label={session.feeIsEstimate ? 'Estimated until session closes' : null}>
            <span className={`font-display text-[14px] num ${session.feeIsEstimate ? 'text-ink-3' : ''}`}>
              {session.feeIsEstimate ? '~' : ''}${formatCurrency(session.fee)}
            </span>
          </Tooltip>
        </div>
        <div className="text-[12px] text-ink-3">
          {session.matter}{' '}
          <span className="text-ink-4">with {session.providerName}</span>
        </div>
      </div>
    );
  }

  // Desktop grid layout matching V4 mock
  return (
    <div
      className={`px-5 py-4 grid grid-cols-12 gap-3 items-center hover:bg-paper-2 transition cursor-pointer ${session.clientIsNew ? 'bg-tide-ss/40' : ''}`}
      onClick={onClick}
    >
      {/* Time */}
      <div className="col-span-2">
        <div className="font-display text-[20px] font-medium num leading-none">
          {localTime}
        </div>
        <div className="text-[11px] text-ink-4 mt-1">
          {session.durationMinutes} min · {methodToLocation(session.format)}
        </div>
      </div>

      {/* Client */}
      <div className="col-span-4 flex items-center gap-2.5">
        <Avatar
          initials={getInitials(session.clientName)}
          className={session.clientIsNew ? 'bg-tide text-paper-hi' : ''}
        />
        <div className="min-w-0">
          <div className="text-[13.5px] font-medium truncate flex items-center gap-1.5">
            {session.clientName}
            {session.clientIsNew && <Badge variant="spark">New</Badge>}
          </div>
          <div
            className={`text-[11.5px] truncate ${session.alertNote ? 'text-rust' : 'text-ink-4'}`}
          >
            {session.alertNote ?? session.clientNote ?? ''}
          </div>
        </div>
      </div>

      {/* Matter + Provider */}
      <div className="col-span-3">
        <div className="text-[13px] text-ink-2">{session.matter}</div>
        <div className="text-[11.5px] text-ink-4">with {session.providerName}</div>
      </div>

      {/* Status */}
      <div className="col-span-2">
        <StatusBadge label={statusInfo.label} tone={statusInfo.tone} />
      </div>

      {/* Fee */}
      <div className="col-span-1 text-right font-display text-[16px] num">
        <Tooltip label={session.feeIsEstimate ? 'Estimated until session closes' : null}>
          <span className={session.feeIsEstimate ? 'text-ink-3' : ''}>
            {session.feeIsEstimate ? '~' : ''}${formatCurrency(session.fee)}
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
